import { GetServerSidePropsContext } from 'next';
import cookie from 'cookie';
import { compact } from 'lodash-es';

import { Box, Flex } from '@chakra-ui/react';
import { Certificate as CertificateComponent } from '@udacity/udacity-certificate';

import { ContentContainer } from '~/components/container';
import { PageCategory } from '~/features/analytics/constants';
import { MarketingMessage } from '~/features/certificates/components/marketing-message';
import { OwnerMessage } from '~/features/certificates/components/owner-message';
import { getCertificateByEnrollmentId } from '~/features/certificates/controllers/get-certificate';
import { useLocalizedCertificate } from '~/features/certificates/hooks/use-localized-certificate';
import { Certificate } from '~/features/certificates/model';
import { setCertificatePublicCacheIfUnauthenticated } from '~/features/certificates/set-certificate-cache';
import { certificatePrintStyles, getCertificateProgramTypeFromSemanticType } from '~/features/certificates/utils';
import { PageHead } from '~/features/page/components/page-head';
import { PageLayout } from '~/features/page/components/page-layout';
import { Page } from '~/features/page/models/page';
import { getInternalPage } from '~/features/page/utils/get-internal-page';
import { getEnrollments } from '~/features/program/controllers/get-enrollments';
import { getProgramContent } from '~/features/program/controllers/get-program-content';
import { ProgramContent } from '~/features/program/models/program-content';
import { getUserPreferences } from '~/features/user/controllers/get-user-preferences';

export default function CertificatePage({
  page,
  fullName,
  initialCertificate,
  programSlug,
  isUsersCertificate,
  initialLanguage,
  enrollmentId,
}: {
  page: Page;
  fullName: string;
  initialCertificate: Certificate;
  programSlug: string | null;
  isUsersCertificate: boolean;
  initialLanguage: string;
  enrollmentId: string;
}) {
  // Fetch localized certificate for unauthenticated users based on browser language
  const { certificate: localizedCertificate } = useLocalizedCertificate({
    enrollmentId,
    initialLanguage,
  });
  const certificate = localizedCertificate || initialCertificate;

  const programType = certificate.semanticType
    ? getCertificateProgramTypeFromSemanticType(certificate.semanticType)
    : undefined;

  return (
    <Box sx={certificatePrintStyles}>
      <style>
        {`
          @media print{
            html, body, #__next {
              overflow: hidden; !important
            }
          }
        `}
      </style>
      <PageHead page={page} pageCategory={PageCategory.Uncategorized} />
      <PageLayout isSimpleMode>
        <ContentContainer>
          <Flex
            gap='24px'
            id='certificate-container'
            flexDir={{
              base: 'column',
              md: 'row',
            }}
          >
            <Box flex='1' id='certificate-wrapper'>
              <CertificateComponent
                certificateType={certificate.type}
                companyLogo={certificate.companyLogo}
                confirmLink={certificate.confirmUrl}
                contract={certificate.contract}
                fullName={fullName}
                graduationDate={certificate.graduationDate}
                partnerLogos={certificate.partnerLogos}
                pdfUrl={certificate.pdfUrl}
                programTitle={certificate.programTitle}
                programType={programType}
                svgUrl={certificate.svgUrl}
              />
            </Box>
            <Box flex='0 2 360px'>
              {isUsersCertificate ? (
                <OwnerMessage certificate={certificate} fullName={fullName} />
              ) : (
                <MarketingMessage programSlug={programSlug} programTitle={certificate?.programTitle} />
              )}
            </Box>
          </Flex>
        </ContentContainer>
      </PageLayout>
    </Box>
  );
}

const CERTIFICATE_CONFIRM_FALLBACK_PREFIX = 'www.udacity.com/certificate/e/';

/**
 * Resolves and updates certificate.confirmUrl for display and the confirm link.
 * If the API confirmUrl points at our own certificate page, we skip fetching to avoid a redirect loop.
 * Otherwise we HEAD the URL with redirect: 'manual'. Only 2xx keeps the API value; any redirect
 * (301/302) or other failure uses the fallback (www.udacity.com/certificate/e/{enrollmentId}).
 * Mutates certificate.confirmUrl in place.
 */
async function resolveCertificateConfirmUrl(
  certificate: { confirmUrl: string },
  enrollmentId: string,
  requestHost: string | undefined
): Promise<void> {
  const confirmFullUrl = `https://${certificate.confirmUrl}`;
  let isOurCertificatePage = false;
  try {
    const parsed = new URL(confirmFullUrl);
    const isCertificatePath = parsed.pathname.startsWith('/certificate/e/');
    const isOurHost = parsed.hostname === 'www.udacity.com' || parsed.hostname === (requestHost ?? '').split(':')[0];
    isOurCertificatePage = isCertificatePath && isOurHost;
  } catch {
    // Invalid URL; proceed to fetch and let it fail if needed
  }

  if (isOurCertificatePage) {
    certificate.confirmUrl = `${CERTIFICATE_CONFIRM_FALLBACK_PREFIX}${enrollmentId}`;
    return;
  }

  const fallback = `${CERTIFICATE_CONFIRM_FALLBACK_PREFIX}${enrollmentId}`;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 5000);
  try {
    const fetchResponse = await fetch(confirmFullUrl, {
      method: 'HEAD',
      signal: controller.signal,
      redirect: 'manual', // Do not follow redirects; a redirect to our app would cause an infinite loop
    });
    const isRedirect = fetchResponse.status === 301 || fetchResponse.status === 302;
    if (isRedirect || !fetchResponse.ok) {
      certificate.confirmUrl = fallback;
    }
  } catch {
    certificate.confirmUrl = fallback;
  } finally {
    clearTimeout(timeoutId);
  }
}

export const getServerSideProps = async ({ req, res, params }: GetServerSidePropsContext) => {
  const cookies = cookie.parse(req.headers.cookie ?? '');
  const jwt = cookies._jwt;

  const enrollmentId = params?.enrollmentId;
  if (!enrollmentId) {
    setCertificatePublicCacheIfUnauthenticated(res, jwt);
    return {
      notFound: true,
    };
  }

  const userPreferences = jwt ? await getUserPreferences({ jwt }) : undefined;
  const initialLanguage = userPreferences?.preferredLanguage ?? 'en-US';

  let certificate: Certificate | null = null;

  try {
    certificate = await getCertificateByEnrollmentId({
      enrollmentId: enrollmentId as string,
      preferredLanguage: initialLanguage,
    });
  } catch {
    setCertificatePublicCacheIfUnauthenticated(res, jwt);
    return {
      notFound: true,
    };
  }

  if (!certificate) {
    setCertificatePublicCacheIfUnauthenticated(res, jwt);
    return {
      notFound: true,
    };
  }

  if (certificate.confirmUrl) {
    await resolveCertificateConfirmUrl(certificate, enrollmentId as string, req.headers.host);
  }

  let isUsersCertificate = false;

  if (jwt) {
    const userEnrollments = await getEnrollments(jwt);
    isUsersCertificate = Boolean(userEnrollments?.some((enrollment) => enrollment.id === enrollmentId));
  }

  const page = getInternalPage({ eventName: 'Certificate Page Viewed', nofollow: false });

  const fullName = compact([certificate?.firstName, certificate?.lastName]).join(' ');

  let programContent: ProgramContent | undefined = undefined;

  if (certificate.programKey) {
    programContent = await getProgramContent(certificate.programKey);
  }

  const programSlug = programContent?.slug || null;

  setCertificatePublicCacheIfUnauthenticated(res, jwt);

  return {
    props: {
      page,
      fullName,
      initialCertificate: certificate,
      programSlug,
      isUsersCertificate,
      initialLanguage,
      enrollmentId,
    },
  };
};
